import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ClockIcon, CheckBadgeIcon, XCircleIcon, SparklesIcon, BuildingOfficeIcon, CurrencyDollarIcon, GiftIcon } from '@heroicons/react/24/solid';
import { useLocation } from 'react-router-dom';

const StatusCard = ({ status, children, icon }) => {
    const colorClasses = {
        pending: 'bg-yellow-100 dark:bg-yellow-900/50 border-yellow-500 text-yellow-800 dark:text-yellow-200',
        approved: 'bg-green-100 dark:bg-green-900/50 border-green-500 text-green-800 dark:text-green-200',
        rejected: 'bg-red-100 dark:bg-red-900/50 border-red-500 text-red-800 dark:text-red-200',
        purchased: 'bg-blue-100 dark:bg-blue-900/50 border-blue-500 text-blue-800 dark:text-blue-200',
        partially_fulfilled: 'bg-purple-100 dark:bg-purple-900/50 border-purple-500 text-purple-800 dark:text-purple-200',
        fulfilled: 'bg-teal-100 dark:bg-teal-900/50 border-teal-500 text-teal-800 dark:text-teal-200',
    };
    return (
        <div className={`p-6 rounded-lg border-l-4 ${colorClasses[status] || 'bg-gray-100'}`}>
            <div className="flex items-center">
                {icon}
                <h3 className="font-bold text-xl ml-3 capitalize">{status.replace('_', ' ')}</h3>
            </div>
            <div className="mt-2 pl-10 text-base">{children}</div>
        </div>
    );
};

export default function OrganizationStatusPage() {
    const { token } = useAuth();
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const location = useLocation();
    const isSidebarOpen = location?.state?.isSidebarOpen;

    useEffect(() => {
        const fetchStatus = async () => {
            if (!token) {
                setLoading(false);
                return;
            }
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/organization/campaigns`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                const contentType = response.headers.get('content-type');
                if (!contentType || !contentType.includes('application/json')) {
                    const text = await response.text();
                    throw new Error(`Server returned HTML instead of JSON: ${text.substring(0, 100)}`);
                }

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Could not fetch your organization campaign status.');
                }
                
                const data = await response.json();
                setCampaigns(data);
            } catch (err) {
                console.error('Error fetching organization campaigns:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchStatus();
    }, [token]);

    if (loading) return <div className="p-8 text-center text-xl">Loading Campaign Status...</div>;
    if (error) return <div className="p-8 text-center text-xl text-red-500">Error: {error}</div>;
    if (!campaigns || campaigns.length === 0) return <div className="p-8 text-center text-xl">Your organization has not created any campaigns yet.</div>;

    return (
        <div className={`p-6 md:p-10 overflow-auto ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
            <h1 className="text-3xl font-extrabold mb-6 text-gray-900 dark:text-white">Organization Campaign Status</h1>
            
            <div className="space-y-6">
                {campaigns.map((campaign) => {
                    const unitPrice = campaign.unit_price || (campaign.total_price / campaign.quantity);
                    const amountRaised = campaign.amount_raised || 0;
                    const unitsFulfilled = campaign.units_fulfilled || 0;
                    const progressPercentage = campaign.total_price > 0 ? (amountRaised / campaign.total_price) * 100 : 0;
                    const unitsPercentage = campaign.quantity > 0 ? (unitsFulfilled / campaign.quantity) * 100 : 0;

                    return (
                        <div key={campaign.id} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center">
                                    <BuildingOfficeIcon className="w-8 h-8 text-orange-500 mr-3" />
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{campaign.product_name}</h2>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            Quantity: {campaign.quantity} units • Total: ₦{campaign.total_price?.toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                                <span className="bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                                    #{campaign.id}
                                </span>
                            </div>
                            
                            <div className="mb-4">
                                <p className="text-gray-500 dark:text-gray-400 mb-2">Organization request reason:</p>
                                <blockquote className="border-l-4 border-orange-500 pl-4 italic text-gray-600 dark:text-gray-300">
                                    {campaign.reason}
                                </blockquote>
                            </div>

                            {/* Campaign Progress Section */}
                            {(campaign.status === 'approved' || campaign.status === 'partially_fulfilled' || campaign.status === 'fulfilled') && (
                                <div className="mb-6 space-y-4">
                                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center">
                                                <CurrencyDollarIcon className="w-5 h-5 text-green-500 mr-2" />
                                                <span className="font-medium text-gray-700 dark:text-gray-300">Funding Progress</span>
                                            </div>
                                            <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                                                {Math.round(progressPercentage)}%
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3">
                                            <div 
                                                className="bg-green-500 h-3 rounded-full transition-all duration-300"
                                                style={{ width: `${progressPercentage}%` }}
                                            ></div>
                                        </div>
                                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
                                            <span>₦{amountRaised.toLocaleString()} raised</span>
                                            <span>₦{campaign.total_price?.toLocaleString()} goal</span>
                                        </div>
                                    </div>

                                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center">
                                                <GiftIcon className="w-5 h-5 text-blue-500 mr-2" />
                                                <span className="font-medium text-gray-700 dark:text-gray-300">Units Fulfilled</span>
                                            </div>
                                            <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                                {Math.round(unitsPercentage)}%
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3">
                                            <div 
                                                className="bg-blue-500 h-3 rounded-full transition-all duration-300"
                                                style={{ width: `${unitsPercentage}%` }}
                                            ></div>
                                        </div>
                                        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
                                            <span>{unitsFulfilled} units received</span>
                                            <span>{campaign.quantity} units requested</span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                                            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                                ₦{unitPrice.toLocaleString()}
                                            </div>
                                            <div className="text-xs text-orange-700 dark:text-orange-300">Price per unit</div>
                                        </div>
                                        <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                                            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                                                {campaign.quantity - unitsFulfilled}
                                            </div>
                                            <div className="text-xs text-purple-700 dark:text-purple-300">Units remaining</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Unit Price</p>
                                    <p className="font-semibold">₦{unitPrice.toLocaleString()}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">Submitted On</p>
                                    <p className="font-semibold">{new Date(campaign.created_at).toLocaleDateString()}</p>
                                </div>
                            </div>

                            <div className="mt-4">
                                {campaign.status === 'pending' && (
                                    <StatusCard status="pending" icon={<ClockIcon className="w-8 h-8"/>}>
                                        Your organization's request is under review by our team. This process typically takes 2-3 business days.
                                    </StatusCard>
                                )}
                                
                                {campaign.status === 'approved' && (
                                    <StatusCard status="approved" icon={<CheckBadgeIcon className="w-8 h-8"/>}>
                                        Your campaign has been approved! It's now visible to donors. 
                                        {campaign.quantity > 1 ? ` Donors can contribute towards part or all of your ${campaign.quantity} units.` : ''}
                                        {amountRaised > 0 && ` So far, ₦${amountRaised.toLocaleString()} has been raised.`}
                                    </StatusCard>
                                )}
                                
                                {campaign.status === 'partially_fulfilled' && (
                                    <StatusCard status="partially_fulfilled" icon={<SparklesIcon className="w-8 h-8"/>}>
                                        {unitsFulfilled} out of {campaign.quantity} units have been funded! 
                                        {amountRaised > 0 && ` ₦${amountRaised.toLocaleString()} has been raised so far.`}
                                        {unitsFulfilled > 0 && ` You have received ${unitsFulfilled} unit${unitsFulfilled !== 1 ? 's' : ''}.`}
                                    </StatusCard>
                                )}
                                
                                {campaign.status === 'fulfilled' && (
                                    <StatusCard status="fulfilled" icon={<SparklesIcon className="w-8 h-8"/>}>
                                        Wonderful news! Your entire request has been funded. 
                                        {campaign.quantity > 1 ? ` All ${campaign.quantity} units have been secured.` : ''}
                                        {amountRaised > 0 && ` Total raised: ₦${amountRaised.toLocaleString()}.`}
                                        Contact us at partnerships@givetechtotheblind.com to arrange collection.
                                    </StatusCard>
                                )}
                                
                                {campaign.status === 'rejected' && (
                                    <StatusCard status="rejected" icon={<XCircleIcon className="w-8 h-8"/>}>
                                        We're sorry, but your organization's campaign request was not approved at this time.
                                        {campaign.admin_notes && (
                                            <div className="mt-2 pt-2 border-t border-red-200 dark:border-red-700">
                                                <p className="font-semibold">Admin Feedback:</p>
                                                <p className="mt-1">{campaign.admin_notes}</p>
                                            </div>
                                        )}
                                    </StatusCard>
                                )}
                            </div>

                            {campaign.updated_at && campaign.status !== 'pending' && (
                                <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                                    Last updated: {new Date(campaign.updated_at).toLocaleString()}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}